import * as React from "react";
import {
    DefaultButton,
    Dialog,
    DialogFooter,
    DialogType,
    Dropdown,
    FontWeights,
    Icon,
    IconButton,
    IDropdownOption,
    Label,
    mergeStyles,
    MessageBar,
    MessageBarType,
    Panel,
    PanelType,
    PrimaryButton,
    Spinner,
    SpinnerSize,
    Stack,
    TextField,
    Toggle,
    TooltipHost,
} from "@fluentui/react";
import { LanguageContext } from "../../utilities/LocalizationModule";
import {
    IHixCategoryMapping,
    IHixDocumentMapping,
    IHixMappingFieldValue,
    IHixTaskMapping,
    IHixTaskMappings,
    IHixUploadTypeOption,
    ISiteDesign,
    ISiteDesignField,
    ISiteDesignLibrary,
    SiteDesignLoadStatus,
} from "../../data-structures/interfaces";
import {
    getTenantHixTaskMappings,
    getTenantHixUploadTypesAdmin,
    getTenantSiteDesign,
    updateTenantHixTaskMappings,
} from "../../utilities/helpers/ApiHelper";
import DialogMessage from "../DialogMessage";
import { MessageType } from "../../data-structures/enums";

interface HixMappingsPanelProps {
    isOpen: boolean;
    /** Tenant int db id — the backend resolves the provisioning GUID server-side. */
    tenantId: number;
    tenantName: string;
    onClose: () => void;
}

// The HIX-standard task types, always shown first in this exact order. The Dutch labels are
// domain terms (identical in every locale), not UI chrome — hence not in LocalizationModule.
const STANDARD_TASK_TYPES: { key: string; label: string }[] = [
    { key: "vat_approval", label: "BTW-aangifte" },
    { key: "ict_approval", label: "ICP-aangifte" },
    { key: "vpb_approval", label: "VPB-aangifte" },
    { key: "ihz_approval", label: "IB-aangifte" },
    { key: "jaarwerk_approval", label: "Publicatiedocument" },
    { key: "wage_tax_approval", label: "Loonbelasting" },
    { key: "document_approval", label: "Documentgoedkeuring" },
    { key: "audit_report", label: "Auditrapport" },
    { key: "sba_message", label: "SBA" },
    { key: "sbr_yearwork", label: "SBR-jaarrekening" },
    { key: "dividend_tax", label: "Dividendbelasting" },
    { key: "yearwork_documents", label: "Jaarwerk" },
];

const customTaskTypeKey = (uploadTypeId: number) => `custom:${uploadTypeId}`;

interface IDocumentTypeDef {
    key: string;
    label: string;
    description: string;
}

// The HIX document (file) types — a fixed list, HIX has no discovery API for these. Labels are
// Dutch domain terms (like STANDARD_TASK_TYPES); descriptions paraphrase the HIX documentation
// and are shown as-is.
const DOCUMENT_TYPE_GROUPS: {
    headerKey: "DocTypesDeclarations" | "DocTypesYearwork" | "DocTypesSystem" | "DocTypesOther";
    types: IDocumentTypeDef[];
}[] = [
    {
        headerKey: "DocTypesDeclarations",
        types: [
            { key: "corporate_tax_declaration", label: "VPB-aangifte", description: "Corporate income tax return (Vennootschapsbelasting / VPB), XBRL." },
            { key: "income_tax_declaration", label: "IB-aangifte", description: "Personal income tax return (Inkomstenbelasting / IB)." },
            { key: "vat_declaration", label: "BTW-aangifte", description: "VAT / turnover tax return (BTW / Omzetbelasting), XBRL." },
            { key: "vat_declaration_supplementation", label: "BTW-suppletie", description: "VAT supplementary return (BTW-suppletie), XBRL." },
            { key: "ict_declaration", label: "ICP-aangifte", description: "Intra-Community Transactions return (ICP / ICT), XBRL." },
            { key: "wage_tax_declaration", label: "Loonbelasting", description: "Payroll/wage tax return (Loonbelasting), XML." },
            { key: "dividend_tax", label: "Dividendbelasting", description: "Dividend tax return, XBRL." },
        ],
    },
    {
        headerKey: "DocTypesYearwork",
        types: [
            { key: "publication_document", label: "Publicatiedocument (XBRL)", description: "Annual accounts filed with the Chamber of Commerce (Jaarrekening), XBRL — the version actually sent to Logius." },
            { key: "publication_document_pdf", label: "Publicatiedocument (PDF, SBR Nexus)", description: "PDF sent to the banks for the SBR Nexus flow." },
            { key: "annual_report", label: "Jaarrapport (PDF)", description: "Annual report as a PDF (not sent to Logius)." },
            { key: "audit_report", label: "Accountantsverklaring", description: "Auditor's statement / accountant's report (Accountantsverklaring), XBRL." },
            { key: "deposit_details", label: "Deponeringsgegevens", description: "KvK deposit/filing details (Deponeringsgegevens), XHTML." },
            { key: "sbr_yearwork", label: "SBR-jaarrekening", description: "SBR-based yearwork document (XBRL) — grouped with yearwork but excluded from the Logius/KvK filenaming flow." },
            { key: "notulen_AVA", label: "Notulen AVA", description: "Minutes of the general shareholders' meeting (Notulen Algemene Vergadering van Aandeelhouders)." },
            { key: "LOR", label: "LOR", description: "Letter of Representation (management representation letter accompanying the audit)." },
        ],
    },
    {
        headerKey: "DocTypesSystem",
        types: [
            { key: "signature_xml", label: "Digitale handtekening (XAdES)", description: "Detached digital signature (XAdES) in XML form." },
            { key: "sepa_salary", label: "SEPA-betaalbestand (salaris)", description: "SEPA payment file for salary payments, XML." },
            { key: "sepa_tax", label: "SEPA-betaalbestand (belasting)", description: "SEPA payment file for tax payments, XML." },
            { key: "sba_message", label: "SBA-bericht", description: "\"Service Berichten Aanslagen\" — tax assessment service messages from the tax authority, XBRL." },
        ],
    },
    {
        headerKey: "DocTypesOther",
        types: [
            { key: "summary", label: "Samenvatting", description: "The human-readable file HIX generates for the user (usually a PDF summary of a declaration)." },
            { key: "audit_log", label: "Auditlog", description: "Audit log of the task as a PDF — generated by HIX, not uploaded by the accountant." },
            { key: "attachment", label: "Bijlage", description: "An extra file added by the accountant (usually a PDF)." },
            { key: "generic_xml", label: "Generieke XML/XBRL", description: "Any XML/XBRL file that doesn't fit one of the specific types." },
        ],
    },
];

// The HIX template/question categories — the same fixed set for both, no discovery API.
// Labels match the HIX UI (see the template/question category dropdowns).
const HIX_CATEGORIES: { key: string; label: string }[] = [
    { key: "bookkeeping", label: "Bookkeeping" },
    { key: "wage", label: "Wages" },
    { key: "ocr", label: "OCR" },
    { key: "fiscal", label: "Fiscal" },
    { key: "other", label: "Other" },
    { key: "yearwork", label: "Yearwork" },
];

// Rail/state key prefix for document-type rows: keys like "sba_message", "audit_report",
// "dividend_tax" and "sbr_yearwork" exist as BOTH a task type and a document type, so document
// rows are namespaced to keep the shared mapping/ignore state collision-free. The prefix never
// reaches the saved payload — document entries are stored under documentTypeKey without it.
// Template/question category rows are namespaced the same way (the two category sets share
// the same keys, so each gets its own prefix).
const DOC_ROW_PREFIX = "doc:";
const documentRowKey = (docTypeKey: string) => `${DOC_ROW_PREFIX}${docTypeKey}`;
const documentTypeKeyFromRow = (rowKey: string) => rowKey.substring(DOC_ROW_PREFIX.length);
const isDocumentRowKey = (rowKey: string) => rowKey.startsWith(DOC_ROW_PREFIX);

const TEMPLATE_CATEGORY_ROW_PREFIX = "tplcat:";
const templateCategoryRowKey = (categoryKey: string) => `${TEMPLATE_CATEGORY_ROW_PREFIX}${categoryKey}`;
const templateCategoryKeyFromRow = (rowKey: string) => rowKey.substring(TEMPLATE_CATEGORY_ROW_PREFIX.length);
const isTemplateCategoryRowKey = (rowKey: string) => rowKey.startsWith(TEMPLATE_CATEGORY_ROW_PREFIX);

const QUESTION_CATEGORY_ROW_PREFIX = "qstcat:";
const questionCategoryRowKey = (categoryKey: string) => `${QUESTION_CATEGORY_ROW_PREFIX}${categoryKey}`;
const questionCategoryKeyFromRow = (rowKey: string) => rowKey.substring(QUESTION_CATEGORY_ROW_PREFIX.length);
const isQuestionCategoryRowKey = (rowKey: string) => rowKey.startsWith(QUESTION_CATEGORY_ROW_PREFIX);

const isTaskRowKey = (rowKey: string) =>
    !isDocumentRowKey(rowKey) && !isTemplateCategoryRowKey(rowKey) && !isQuestionCategoryRowKey(rowKey);

const DOCUMENT_TYPE_BY_ROW_KEY = new Map<string, IDocumentTypeDef>(
    DOCUMENT_TYPE_GROUPS.flatMap((g) => g.types).map((t) => [documentRowKey(t.key), t])
);

const KNOWN_CATEGORY_KEYS = new Set(HIX_CATEGORIES.map((c) => c.key));

// One mapping per (task type, site-design type); state is keyed on the composite.
const mappingKey = (taskTypeKey: string, typeKey: string) => `${taskTypeKey}::${typeKey}`;

// Document and category mappings are held in the same in-memory record (and edited by the
// same machinery) as task mappings, carrying the prefixed row key in taskTypeKey; they are
// converted back to their clean saved shapes on save/canonicalize.
const toInternalDocumentMapping = (m: IHixDocumentMapping): IHixTaskMapping => ({
    taskTypeKey: documentRowKey(m.documentTypeKey),
    isCustom: false,
    typeKey: m.typeKey,
    libraryTitle: m.libraryTitle,
    fields: m.fields ?? [],
});

const toInternalCategoryMapping = (m: IHixCategoryMapping, rowKey: (categoryKey: string) => string): IHixTaskMapping => ({
    taskTypeKey: rowKey(m.categoryKey),
    isCustom: false,
    typeKey: m.typeKey,
    libraryTitle: m.libraryTitle,
    fields: m.fields ?? [],
});

// Field types that can never carry an authored value.
const EXCLUDED_FIELD_TYPES = ["Computed", "LookupMulti"];

interface IMappingRow {
    /**
     * Unique rail/state key: task type key, "custom:{id}", "doc:{documentTypeKey}",
     * "tplcat:{categoryKey}" or "qstcat:{categoryKey}".
     */
    key: string;
    label: string;
    kind: "task" | "document" | "templateCategory" | "questionCategory";
    isCustom: boolean;
    hixUploadTypeId?: number;
    /** Saved custom mapping whose upload type HIX no longer returns. */
    isStale: boolean;
}

interface ITypeSection {
    key: string;
    /** The type's libraries from the site design; empty for a type only known from saved data. */
    libraries: ISiteDesignLibrary[];
}

const layoutStyle = mergeStyles({
    display: "flex",
    gap: 16,
    height: "calc(100vh - 210px)",
    marginTop: 10,
});

const railStyle = mergeStyles({
    borderRight: "1px solid var(--db-border)",
    display: "flex",
    flexDirection: "column",
    flexShrink: 0,
    gap: 4,
    overflowY: "auto",
    paddingRight: 12,
    width: 320,
});

const railGroupHeaderStyle = mergeStyles({
    color: "var(--db-fg)",
    fontSize: 12,
    fontWeight: FontWeights.semibold,
    marginTop: 10,
    opacity: 0.8,
    textTransform: "uppercase",
});

/** Divider header separating the task-type rows from the document-type rows. */
const railSectionHeaderStyle = mergeStyles(railGroupHeaderStyle, {
    borderTop: "1px solid var(--db-border)",
    marginTop: 18,
    paddingTop: 14,
});

const railRowStyle = mergeStyles({
    alignItems: "center",
    background: "transparent",
    border: "1px solid transparent",
    borderRadius: 6,
    cursor: "pointer",
    display: "flex",
    gap: 8,
    justifyContent: "space-between",
    padding: "8px 10px",
    textAlign: "left",
    width: "100%",
    selectors: {
        ":hover": { backgroundColor: "var(--db-surface)" },
    },
});

const railRowSelectedStyle = mergeStyles({
    backgroundColor: "var(--db-surface-raised)",
    border: "1px solid var(--db-border)",
});

const railRowLabelStyle = mergeStyles({
    color: "var(--db-fg)",
    fontSize: 13,
    minWidth: 0,
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
});

const pillBaseStyle = mergeStyles({
    alignItems: "center",
    borderRadius: 8,
    display: "inline-flex",
    flexShrink: 0,
    fontSize: 11,
    fontWeight: FontWeights.semibold,
    justifyContent: "center",
    maxWidth: 130,
    overflow: "hidden",
    padding: "2px 8px",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
});

const mappedPillStyle = mergeStyles(pillBaseStyle, {
    backgroundColor: "var(--db-pill-info-bg)",
    border: "1px solid var(--db-pill-info-border)",
    color: "var(--db-pill-info-fg)",
});

const unmappedPillStyle = mergeStyles(pillBaseStyle, {
    backgroundColor: "var(--db-pill-neutral-bg)",
    border: "1px solid var(--db-pill-neutral-border)",
    color: "var(--db-pill-neutral-fg)",
});

const detailPaneStyle = mergeStyles({
    flexGrow: 1,
    minWidth: 0,
    overflowY: "auto",
    paddingRight: 16,
});

const typeSectionStyle = mergeStyles({
    backgroundColor: "var(--db-surface-raised)",
    border: "1px solid var(--db-border)",
    borderRadius: 8,
    boxSizing: "border-box",
    padding: "12px 16px 16px 16px",
});

const warningIconStyle = mergeStyles({
    color: "var(--db-pill-warning-fg)",
    flexShrink: 0,
});

export const HixMappingsPanel: React.FunctionComponent<HixMappingsPanelProps> = ({
    isOpen,
    tenantId,
    tenantName,
    onClose,
}) => {
    const languageStrings = React.useContext(LanguageContext);

    const [isLoading, setIsLoading] = React.useState<boolean>(true);
    const [siteDesignStatus, setSiteDesignStatus] = React.useState<SiteDesignLoadStatus>("ok");
    const [siteDesign, setSiteDesign] = React.useState<ISiteDesign | undefined>(undefined);
    const [hixConfigured, setHixConfigured] = React.useState<boolean>(false);
    const [customUploadTypes, setCustomUploadTypes] = React.useState<IHixUploadTypeOption[]>([]);
    const [loadedMappings, setLoadedMappings] = React.useState<IHixTaskMapping[]>([]);
    const [loadedDocumentMappings, setLoadedDocumentMappings] = React.useState<IHixDocumentMapping[]>([]);
    const [loadedTemplateCategoryMappings, setLoadedTemplateCategoryMappings] = React.useState<IHixCategoryMapping[]>([]);
    const [loadedQuestionCategoryMappings, setLoadedQuestionCategoryMappings] = React.useState<IHixCategoryMapping[]>([]);
    /** Ignored keys as merged rail keys: task keys verbatim, document/category keys prefixed. */
    const [loadedIgnored, setLoadedIgnored] = React.useState<string[]>([]);
    const [mappingsLoadFailed, setMappingsLoadFailed] = React.useState<boolean>(false);
    const [mappingByKey, setMappingByKey] = React.useState<Record<string, IHixTaskMapping>>({});
    const [ignoredKeys, setIgnoredKeys] = React.useState<Set<string>>(new Set());
    const [selectedKey, setSelectedKey] = React.useState<string>(STANDARD_TASK_TYPES[0].key);
    const [isSaving, setIsSaving] = React.useState<boolean>(false);
    const [saveFailed, setSaveFailed] = React.useState<boolean>(false);
    const [showCloseConfirm, setShowCloseConfirm] = React.useState<boolean>(false);
    /** Source typeKey of a pending "Copy to other types" awaiting confirmation. */
    const [copyConfirmSource, setCopyConfirmSource] = React.useState<string | null>(null);

    React.useEffect(() => {
        let cancelled = false;
        (async () => {
            const [designResult, uploadTypesResult, mappingsResult] = await Promise.all([
                getTenantSiteDesign(tenantId),
                getTenantHixUploadTypesAdmin(tenantId),
                getTenantHixTaskMappings(tenantId),
            ]);
            if (cancelled) {
                return;
            }

            setSiteDesignStatus(designResult.status);
            setSiteDesign(designResult.design);

            setHixConfigured(uploadTypesResult?.hixConfigured === true);
            // Dedupe defensively by id and case-insensitive name.
            const seenIds = new Set<number>();
            const seenNames = new Set<string>();
            const uniqueTypes = (uploadTypesResult?.uploadTypes ?? []).filter((t) => {
                const nameKey = t.name?.trim()?.toLowerCase() ?? "";
                if (!t.id || !nameKey || seenIds.has(t.id) || seenNames.has(nameKey)) {
                    return false;
                }
                seenIds.add(t.id);
                seenNames.add(nameKey);
                return true;
            });
            setCustomUploadTypes(uniqueTypes);

            // Entries without a typeKey are from the pre-per-type dev shape — dropped on load
            // (the feature is unreleased; no migration).
            const savedMappings = (mappingsResult === undefined ? [] : mappingsResult?.mappings ?? []).filter(
                (m) => m?.taskTypeKey && m?.typeKey
            );
            setMappingsLoadFailed(mappingsResult === undefined);
            setLoadedMappings(savedMappings);
            const savedDocumentMappings = (mappingsResult === undefined ? [] : mappingsResult?.documentMappings ?? []).filter(
                (m) => m?.documentTypeKey && m?.typeKey
            );
            setLoadedDocumentMappings(savedDocumentMappings);
            const savedTemplateCategoryMappings = (
                mappingsResult === undefined ? [] : mappingsResult?.templateCategoryMappings ?? []
            ).filter((m) => m?.categoryKey && m?.typeKey);
            setLoadedTemplateCategoryMappings(savedTemplateCategoryMappings);
            const savedQuestionCategoryMappings = (
                mappingsResult === undefined ? [] : mappingsResult?.questionCategoryMappings ?? []
            ).filter((m) => m?.categoryKey && m?.typeKey);
            setLoadedQuestionCategoryMappings(savedQuestionCategoryMappings);
            const record: Record<string, IHixTaskMapping> = {};
            savedMappings.forEach((m) => {
                record[mappingKey(m.taskTypeKey, m.typeKey)] = { ...m, fields: m.fields ?? [] };
            });
            savedDocumentMappings.forEach((m) => {
                record[mappingKey(documentRowKey(m.documentTypeKey), m.typeKey)] = toInternalDocumentMapping(m);
            });
            savedTemplateCategoryMappings.forEach((m) => {
                record[mappingKey(templateCategoryRowKey(m.categoryKey), m.typeKey)] = toInternalCategoryMapping(m, templateCategoryRowKey);
            });
            savedQuestionCategoryMappings.forEach((m) => {
                record[mappingKey(questionCategoryRowKey(m.categoryKey), m.typeKey)] = toInternalCategoryMapping(m, questionCategoryRowKey);
            });
            setMappingByKey(record);
            const prefixIgnored = (keys: unknown[] | undefined, rowKey: (k: string) => string) =>
                (mappingsResult === undefined ? [] : keys ?? []).map((k) => (typeof k === "string" ? rowKey(k) : k));
            const savedIgnored = Array.from(
                new Set(
                    [
                        ...(mappingsResult === undefined ? [] : mappingsResult?.ignoredTaskTypes ?? []),
                        ...prefixIgnored(mappingsResult?.ignoredDocumentTypes, documentRowKey),
                        ...prefixIgnored(mappingsResult?.ignoredTemplateCategories, templateCategoryRowKey),
                        ...prefixIgnored(mappingsResult?.ignoredQuestionCategories, questionCategoryRowKey),
                    ].filter((k): k is string => typeof k === "string" && !!k)
                )
            );
            setLoadedIgnored(savedIgnored);
            setIgnoredKeys(new Set(savedIgnored));
            setIsLoading(false);
        })();
        return () => {
            cancelled = true;
        };
    }, [tenantId]);

    // Row model: standard task types first (fixed order), then live custom types, then saved
    // custom mappings whose upload type HIX no longer returns (never silently dropped), then
    // the fixed document types, then saved document mappings whose type key this build does not
    // know (saved by newer code — also never silently dropped).
    const rows = React.useMemo<IMappingRow[]>(() => {
        const standardRows: IMappingRow[] = STANDARD_TASK_TYPES.map((t) => ({
            key: t.key,
            label: t.label,
            kind: "task",
            isCustom: false,
            isStale: false,
        }));
        const customRows: IMappingRow[] = customUploadTypes.map((t) => ({
            key: customTaskTypeKey(t.id),
            label: t.name,
            kind: "task",
            isCustom: true,
            hixUploadTypeId: t.id,
            isStale: false,
        }));
        const knownKeys = new Set<string>([...standardRows, ...customRows].map((r) => r.key));
        const staleByKey = new Map<string, IMappingRow>();
        loadedMappings
            .filter((m) => m.isCustom && !knownKeys.has(m.taskTypeKey))
            .forEach((m) => {
                if (!staleByKey.has(m.taskTypeKey)) {
                    staleByKey.set(m.taskTypeKey, {
                        key: m.taskTypeKey,
                        label: m.taskTypeLabel ?? m.taskTypeKey,
                        kind: "task",
                        isCustom: true,
                        hixUploadTypeId: m.hixUploadTypeId,
                        // Only flag "no longer in HIX" when we actually got the live list.
                        isStale: hixConfigured,
                    });
                }
            });
        const documentRows: IMappingRow[] = DOCUMENT_TYPE_GROUPS.flatMap((g) => g.types).map((t) => ({
            key: documentRowKey(t.key),
            label: t.label,
            kind: "document",
            isCustom: false,
            isStale: false,
        }));
        const unknownDocRows: IMappingRow[] = [];
        loadedDocumentMappings
            .filter((m) => !DOCUMENT_TYPE_BY_ROW_KEY.has(documentRowKey(m.documentTypeKey)))
            .forEach((m) => {
                const key = documentRowKey(m.documentTypeKey);
                if (!unknownDocRows.some((r) => r.key === key)) {
                    unknownDocRows.push({
                        key,
                        label: m.documentTypeKey,
                        kind: "document",
                        isCustom: false,
                        isStale: false,
                    });
                }
            });
        // The fixed categories, then saved category mappings whose key this build does not know
        // (saved by newer code — never silently dropped). Template and question categories share
        // the same fixed set but are independent rows.
        const categoryRows = (
            kind: "templateCategory" | "questionCategory",
            rowKey: (categoryKey: string) => string,
            loaded: IHixCategoryMapping[]
        ): IMappingRow[] => {
            const known: IMappingRow[] = HIX_CATEGORIES.map((c) => ({
                key: rowKey(c.key),
                label: c.label,
                kind,
                isCustom: false,
                isStale: false,
            }));
            loaded
                .filter((m) => !KNOWN_CATEGORY_KEYS.has(m.categoryKey))
                .forEach((m) => {
                    const key = rowKey(m.categoryKey);
                    if (!known.some((r) => r.key === key)) {
                        known.push({ key, label: m.categoryKey, kind, isCustom: false, isStale: false });
                    }
                });
            return known;
        };
        return [
            ...standardRows,
            ...customRows,
            ...Array.from(staleByKey.values()),
            ...documentRows,
            ...unknownDocRows,
            ...categoryRows("templateCategory", templateCategoryRowKey, loadedTemplateCategoryMappings),
            ...categoryRows("questionCategory", questionCategoryRowKey, loadedQuestionCategoryMappings),
        ];
    }, [
        customUploadTypes,
        loadedMappings,
        loadedDocumentMappings,
        loadedTemplateCategoryMappings,
        loadedQuestionCategoryMappings,
        hixConfigured,
    ]);

    // One section per site-design type (Bedrijf / Persoon / Overig ...), in sortOrder; types
    // that only exist in saved mappings (design changed since save) are appended with no
    // libraries so the saved data stays visible instead of vanishing.
    const typeSections = React.useMemo<ITypeSection[]>(() => {
        const sections: ITypeSection[] = [...(siteDesign?.types ?? [])]
            .filter((t) => t?.key)
            .sort((a, b) => a.sortOrder - b.sortOrder)
            .map((t) => ({ key: t.key, libraries: t.libraries ?? [] }));
        const knownTypeKeys = new Set(sections.map((s) => s.key.toLowerCase()));
        [
            ...loadedMappings,
            ...loadedDocumentMappings,
            ...loadedTemplateCategoryMappings,
            ...loadedQuestionCategoryMappings,
        ].forEach((m) => {
            if (m.typeKey && !knownTypeKeys.has(m.typeKey.toLowerCase())) {
                knownTypeKeys.add(m.typeKey.toLowerCase());
                sections.push({ key: m.typeKey, libraries: [] });
            }
        });
        return sections;
    }, [siteDesign, loadedMappings, loadedDocumentMappings, loadedTemplateCategoryMappings, loadedQuestionCategoryMappings]);

    const canEdit = siteDesignStatus === "ok" && !mappingsLoadFailed;

    // Canonical serialization: mapped entries only, in row order then type order, fields
    // sorted by internalName, ignored keys sorted — so reordering-only state churn never
    // arms Save. Document entries are canonicalized in their clean saved shape (no labels,
    // no custom/upload-type fields) so load-vs-edit label differences never arm Save either.
    const canonicalize = React.useCallback(
        (record: Record<string, IHixTaskMapping>, ignored: ReadonlySet<string>): string => {
            const canonicalFields = (m: IHixTaskMapping) =>
                [...(m.fields ?? [])]
                    .filter((f) => f.internalName)
                    .sort((a, b) => a.internalName.localeCompare(b.internalName))
                    .map((f) => ({ internalName: f.internalName, value: f.value ?? "" }));
            const mapped = (kind: IMappingRow["kind"]) =>
                rows
                    .filter((row) => row.kind === kind)
                    .flatMap((row) => typeSections.map((section) => record[mappingKey(row.key, section.key)]))
                    .filter((m) => m?.libraryTitle);
            return JSON.stringify({
                ignored: Array.from(ignored).sort(),
                mappings: mapped("task").map((m) => ({
                    taskTypeKey: m.taskTypeKey,
                    taskTypeLabel: m.taskTypeLabel,
                    isCustom: m.isCustom,
                    hixUploadTypeId: m.hixUploadTypeId,
                    typeKey: m.typeKey,
                    libraryTitle: m.libraryTitle,
                    fields: canonicalFields(m),
                })),
                documentMappings: mapped("document").map((m) => ({
                    documentTypeKey: documentTypeKeyFromRow(m.taskTypeKey),
                    typeKey: m.typeKey,
                    libraryTitle: m.libraryTitle,
                    fields: canonicalFields(m),
                })),
                templateCategoryMappings: mapped("templateCategory").map((m) => ({
                    categoryKey: templateCategoryKeyFromRow(m.taskTypeKey),
                    typeKey: m.typeKey,
                    libraryTitle: m.libraryTitle,
                    fields: canonicalFields(m),
                })),
                questionCategoryMappings: mapped("questionCategory").map((m) => ({
                    categoryKey: questionCategoryKeyFromRow(m.taskTypeKey),
                    typeKey: m.typeKey,
                    libraryTitle: m.libraryTitle,
                    fields: canonicalFields(m),
                })),
            });
        },
        [rows, typeSections]
    );

    const initialCanonical = React.useMemo(() => {
        const record: Record<string, IHixTaskMapping> = {};
        loadedMappings.forEach((m) => {
            record[mappingKey(m.taskTypeKey, m.typeKey)] = m;
        });
        loadedDocumentMappings.forEach((m) => {
            record[mappingKey(documentRowKey(m.documentTypeKey), m.typeKey)] = toInternalDocumentMapping(m);
        });
        loadedTemplateCategoryMappings.forEach((m) => {
            record[mappingKey(templateCategoryRowKey(m.categoryKey), m.typeKey)] = toInternalCategoryMapping(m, templateCategoryRowKey);
        });
        loadedQuestionCategoryMappings.forEach((m) => {
            record[mappingKey(questionCategoryRowKey(m.categoryKey), m.typeKey)] = toInternalCategoryMapping(m, questionCategoryRowKey);
        });
        return canonicalize(record, new Set(loadedIgnored));
    }, [
        loadedMappings,
        loadedDocumentMappings,
        loadedTemplateCategoryMappings,
        loadedQuestionCategoryMappings,
        loadedIgnored,
        canonicalize,
    ]);

    const isDirty = !isLoading && canonicalize(mappingByKey, ignoredKeys) !== initialCanonical;

    const selectedRow = rows.find((r) => r.key === selectedKey) ?? rows[0];

    const isSelectedIgnored = selectedRow ? ignoredKeys.has(selectedRow.key) : false;
    // Ignored task/document types keep their mappings (so un-ignoring restores them) but are
    // not editable.
    const canEditSelected = canEdit && !isSelectedIgnored;
    const selectedDocDef = selectedRow?.kind === "document" ? DOCUMENT_TYPE_BY_ROW_KEY.get(selectedRow.key) : undefined;

    const toggleIgnored = (rowKey: string, ignored: boolean) => {
        setIgnoredKeys((prev) => {
            const next = new Set(prev);
            if (ignored) {
                next.add(rowKey);
            } else {
                next.delete(rowKey);
            }
            return next;
        });
    };

    const getMapping = (typeKey: string): IHixTaskMapping | undefined =>
        selectedRow ? mappingByKey[mappingKey(selectedRow.key, typeKey)] : undefined;

    const updateMapping = (typeKey: string, updater: (current: IHixTaskMapping | undefined) => IHixTaskMapping | undefined) => {
        if (!selectedRow) {
            return;
        }
        const key = mappingKey(selectedRow.key, typeKey);
        setMappingByKey((prev) => {
            const next = { ...prev };
            const updated = updater(prev[key]);
            if (updated) {
                next[key] = updated;
            } else {
                delete next[key];
            }
            return next;
        });
    };

    const onLibraryChange = (typeKey: string, option?: IDropdownOption) => {
        const libraryTitle = (option?.key as string) ?? "";
        updateMapping(typeKey, () => {
            if (!libraryTitle) {
                // "Unmapped" clears the whole entry, including its field values.
                return undefined;
            }
            return {
                taskTypeKey: selectedRow.key,
                taskTypeLabel: selectedRow.label,
                isCustom: selectedRow.isCustom,
                hixUploadTypeId: selectedRow.hixUploadTypeId,
                typeKey,
                libraryTitle,
                fields: [],
            };
        });
    };

    const updateField = (typeKey: string, index: number, patch: Partial<IHixMappingFieldValue>) => {
        updateMapping(typeKey, (current) => {
            if (!current) {
                return current;
            }
            const fields = current.fields.map((f, i) => (i === index ? { ...f, ...patch } : f));
            return { ...current, fields };
        });
    };

    const removeField = (typeKey: string, index: number) => {
        updateMapping(typeKey, (current) => {
            if (!current) {
                return current;
            }
            return { ...current, fields: current.fields.filter((_, i) => i !== index) };
        });
    };

    const addField = (typeKey: string) => {
        updateMapping(typeKey, (current) => {
            if (!current) {
                return current;
            }
            return { ...current, fields: [...current.fields, { internalName: "", value: "" }] };
        });
    };

    // Per-target preview for the copy confirmation dialog: what will be set, what an existing
    // mapping being overwritten looks like, and what cannot travel.
    const buildCopyPreview = (sourceTypeKey: string) => {
        const source = getMapping(sourceTypeKey);
        if (!source?.libraryTitle || !selectedRow) {
            return [];
        }
        const sourceFields = (source.fields ?? []).filter((f) => f.internalName);
        return typeSections
            .filter((section) => section.key !== sourceTypeKey)
            .map((section) => {
                const targetLibrary = section.libraries.find(
                    (l) => l.title.toLowerCase() === source.libraryTitle.toLowerCase()
                );
                if (!targetLibrary) {
                    return { typeKey: section.key, willSet: false, text: languageStrings.CopyNotAvailable };
                }
                const targetFieldNames = new Set(targetLibrary.fields.map((f) => f.internalName));
                const copyableCount = sourceFields.filter((f) => targetFieldNames.has(f.internalName)).length;
                const skippedCount = sourceFields.length - copyableCount;
                const existing = mappingByKey[mappingKey(selectedRow.key, section.key)];
                let text = `"${targetLibrary.title}" (${copyableCount} ${languageStrings.FieldsSuffix})`;
                if (existing?.libraryTitle) {
                    text += ` — ${languageStrings.CopyOverwritesExisting} ("${existing.libraryTitle}")`;
                }
                if (skippedCount > 0) {
                    text += ` — ${skippedCount} ${languageStrings.CopyFieldsSkipped}`;
                }
                return { typeKey: section.key, willSet: true, text };
            });
    };

    // Copies a section's mapping to every OTHER type that has a library with the same title;
    // only fields that exist (by internalName) in the target type's library travel with it.
    const copyToOtherTypes = (sourceTypeKey: string) => {
        const source = getMapping(sourceTypeKey);
        if (!source?.libraryTitle || !selectedRow) {
            return;
        }
        setMappingByKey((prev) => {
            const next = { ...prev };
            typeSections
                .filter((section) => section.key !== sourceTypeKey)
                .forEach((section) => {
                    const targetLibrary = section.libraries.find(
                        (l) => l.title.toLowerCase() === source.libraryTitle.toLowerCase()
                    );
                    if (!targetLibrary) {
                        return;
                    }
                    const targetFieldNames = new Set(targetLibrary.fields.map((f) => f.internalName));
                    next[mappingKey(selectedRow.key, section.key)] = {
                        taskTypeKey: selectedRow.key,
                        taskTypeLabel: selectedRow.label,
                        isCustom: selectedRow.isCustom,
                        hixUploadTypeId: selectedRow.hixUploadTypeId,
                        typeKey: section.key,
                        libraryTitle: targetLibrary.title,
                        fields: (source.fields ?? [])
                            .filter((f) => f.internalName && targetFieldNames.has(f.internalName))
                            .map((f) => ({ ...f })),
                    };
                });
            return next;
        });
    };

    const onSave = async () => {
        setIsSaving(true);
        setSaveFailed(false);
        const mappedEntries = (kind: IMappingRow["kind"]) =>
            rows
                .filter((row) => row.kind === kind)
                .flatMap((row) => typeSections.map((section) => mappingByKey[mappingKey(row.key, section.key)]))
                .filter((m) => m?.libraryTitle);
        const payload: IHixTaskMappings = {
            version: 1,
            updatedAtUtc: new Date().toISOString(),
            mappings: mappedEntries("task").map((m) => ({
                ...m,
                fields: (m.fields ?? []).filter((f) => f.internalName),
            })),
            ignoredTaskTypes: Array.from(ignoredKeys).filter(isTaskRowKey).sort(),
            documentMappings: mappedEntries("document").map((m) => ({
                documentTypeKey: documentTypeKeyFromRow(m.taskTypeKey),
                typeKey: m.typeKey,
                libraryTitle: m.libraryTitle,
                fields: (m.fields ?? []).filter((f) => f.internalName),
            })),
            ignoredDocumentTypes: Array.from(ignoredKeys)
                .filter(isDocumentRowKey)
                .map(documentTypeKeyFromRow)
                .sort(),
            templateCategoryMappings: mappedEntries("templateCategory").map((m) => ({
                categoryKey: templateCategoryKeyFromRow(m.taskTypeKey),
                typeKey: m.typeKey,
                libraryTitle: m.libraryTitle,
                fields: (m.fields ?? []).filter((f) => f.internalName),
            })),
            ignoredTemplateCategories: Array.from(ignoredKeys)
                .filter(isTemplateCategoryRowKey)
                .map(templateCategoryKeyFromRow)
                .sort(),
            questionCategoryMappings: mappedEntries("questionCategory").map((m) => ({
                categoryKey: questionCategoryKeyFromRow(m.taskTypeKey),
                typeKey: m.typeKey,
                libraryTitle: m.libraryTitle,
                fields: (m.fields ?? []).filter((f) => f.internalName),
            })),
            ignoredQuestionCategories: Array.from(ignoredKeys)
                .filter(isQuestionCategoryRowKey)
                .map(questionCategoryKeyFromRow)
                .sort(),
        };
        const success = await updateTenantHixTaskMappings(tenantId, payload);
        setIsSaving(false);
        if (success) {
            onClose();
        } else {
            setSaveFailed(true);
        }
    };

    const attemptClose = () => {
        if (isDirty) {
            setShowCloseConfirm(true);
        } else {
            onClose();
        }
    };

    const buildLibraryOptions = (section: ITypeSection, mapping?: IHixTaskMapping): IDropdownOption[] => {
        const options: IDropdownOption[] = [{ key: "", text: languageStrings.Unmapped }];
        section.libraries.forEach((library) => {
            options.push({
                key: library.title,
                text: library.isDeprecated ? `${library.title} ${languageStrings.DeprecatedSuffix}` : library.title,
            });
        });
        // Keep a stale saved library selectable so the admin can see (and fix) it.
        if (
            mapping?.libraryTitle &&
            !section.libraries.some((l) => l.title.toLowerCase() === mapping.libraryTitle.toLowerCase())
        ) {
            options.push({
                key: mapping.libraryTitle,
                text: `${mapping.libraryTitle} — ${languageStrings.NoLongerInSiteDesign}`,
            });
        }
        return options;
    };

    const renderValueEditor = (
        typeKey: string,
        fieldValue: IHixMappingFieldValue,
        index: number,
        fieldDef?: ISiteDesignField
    ) => {
        const onValueChange = (value: string) => updateField(typeKey, index, { value });
        if (!fieldDef) {
            // Saved field that no longer exists in this type's library: editable as raw text
            // so the admin can still see the value, flagged with a warning next to the row.
            return <TextField value={fieldValue.value} disabled={!canEditSelected} onChange={(_, v) => onValueChange(v ?? "")} />;
        }
        switch (fieldDef.fieldType) {
            case "Choice":
                return (
                    <Dropdown
                        options={(fieldDef.choices ?? []).map((c) => ({ key: c, text: c }))}
                        selectedKey={fieldValue.value || undefined}
                        disabled={!canEditSelected}
                        onChange={(_, option) => onValueChange((option?.key as string) ?? "")}
                    />
                );
            case "Boolean":
                return (
                    <Toggle
                        checked={fieldValue.value === "true"}
                        disabled={!canEditSelected}
                        onText={languageStrings.Yes}
                        offText={languageStrings.No}
                        styles={{ root: { marginBottom: 0 } }}
                        onChange={(_, checked) => onValueChange(checked ? "true" : "false")}
                    />
                );
            case "Number":
                return (
                    <TextField
                        type="number"
                        value={fieldValue.value}
                        disabled={!canEditSelected}
                        onChange={(_, v) => onValueChange(v ?? "")}
                    />
                );
            case "Note":
                return (
                    <TextField
                        multiline
                        rows={2}
                        value={fieldValue.value}
                        disabled={!canEditSelected}
                        onChange={(_, v) => onValueChange(v ?? "")}
                    />
                );
            case "DateTime":
                return (
                    <TextField
                        placeholder="2026-01-31T00:00:00Z"
                        value={fieldValue.value}
                        disabled={!canEditSelected}
                        onChange={(_, v) => onValueChange(v ?? "")}
                    />
                );
            default:
                return <TextField value={fieldValue.value} disabled={!canEditSelected} onChange={(_, v) => onValueChange(v ?? "")} />;
        }
    };

    const renderFieldRow = (
        section: ITypeSection,
        mapping: IHixTaskMapping,
        chosenLibrary: ISiteDesignLibrary | undefined,
        fieldValue: IHixMappingFieldValue,
        index: number
    ) => {
        const selectableFields = (chosenLibrary?.fields ?? []).filter((f) => !EXCLUDED_FIELD_TYPES.includes(f.fieldType));
        const fieldDef = selectableFields.find((f) => f.internalName === fieldValue.internalName);
        const isStaleField = !!fieldValue.internalName && !!chosenLibrary && !fieldDef;
        const assignedNames = new Set(mapping.fields.map((f) => f.internalName));
        const fieldOptions: IDropdownOption[] = selectableFields
            .filter((f) => f.internalName === fieldValue.internalName || !assignedNames.has(f.internalName))
            .map((f) => ({
                key: f.internalName,
                // "*" marks fields the library schema requires.
                text: `${f.displayName || f.internalName}${f.required ? " *" : ""}`,
            }));
        if (isStaleField) {
            fieldOptions.push({
                key: fieldValue.internalName,
                text: `${fieldValue.internalName} — ${languageStrings.NoLongerInSiteDesign}`,
            });
        }
        return (
            <Stack key={index} horizontal tokens={{ childrenGap: 10 }} verticalAlign="start">
                <Stack.Item styles={{ root: { width: 300 } }}>
                    <Dropdown
                        options={fieldOptions}
                        selectedKey={fieldValue.internalName || undefined}
                        placeholder={languageStrings.AddMetadataField}
                        disabled={!canEditSelected}
                        onChange={(_, option) =>
                            updateField(section.key, index, { internalName: (option?.key as string) ?? "", value: "" })
                        }
                    />
                </Stack.Item>
                <Stack.Item grow styles={{ root: { minWidth: 0, maxWidth: 400 } }}>
                    {renderValueEditor(section.key, fieldValue, index, fieldDef)}
                </Stack.Item>
                {isStaleField && (
                    <TooltipHost content={languageStrings.NoLongerInSiteDesign}>
                        <Icon iconName="Warning" className={warningIconStyle} style={{ marginTop: 8 }} />
                    </TooltipHost>
                )}
                <IconButton
                    iconProps={{ iconName: "Delete" }}
                    ariaLabel={languageStrings.Delete}
                    disabled={!canEditSelected}
                    onClick={() => removeField(section.key, index)}
                />
            </Stack>
        );
    };

    const renderTypeSection = (section: ITypeSection) => {
        const mapping = getMapping(section.key);
        const chosenLibrary = mapping?.libraryTitle
            ? section.libraries.find((l) => l.title.toLowerCase() === mapping.libraryTitle.toLowerCase())
            : undefined;
        const staleLibrary = !!mapping?.libraryTitle && !chosenLibrary;
        // A section is editable only when the design actually carries this type; a type known
        // solely from saved data keeps its values visible but cannot be authored against.
        const sectionEditable = canEditSelected && section.libraries.length > 0;
        return (
            <div key={section.key} className={typeSectionStyle}>
                <Stack tokens={{ childrenGap: 10 }}>
                    <Stack horizontal verticalAlign="center" horizontalAlign="space-between">
                        <Stack horizontal verticalAlign="center" tokens={{ childrenGap: 6 }}>
                            <Label styles={{ root: { fontSize: 14, fontWeight: FontWeights.semibold, padding: 0 } }}>
                                {section.key}
                            </Label>
                            {staleLibrary && (
                                <TooltipHost content={languageStrings.NoLongerInSiteDesign}>
                                    <Icon iconName="Warning" className={warningIconStyle} />
                                </TooltipHost>
                            )}
                        </Stack>
                        {mapping?.libraryTitle && typeSections.length > 1 && (
                            <DefaultButton
                                iconProps={{ iconName: "Copy" }}
                                text={languageStrings.CopyToOtherTypes}
                                disabled={!canEditSelected}
                                onClick={() => setCopyConfirmSource(section.key)}
                            />
                        )}
                    </Stack>
                    <Stack.Item styles={{ root: { maxWidth: 400 } }}>
                        <Dropdown
                            label={languageStrings.DestinationLibrary}
                            options={buildLibraryOptions(section, mapping)}
                            selectedKey={mapping?.libraryTitle ?? ""}
                            disabled={isSelectedIgnored || (!sectionEditable && !mapping?.libraryTitle)}
                            onChange={(_, option) => onLibraryChange(section.key, option)}
                        />
                    </Stack.Item>
                    {mapping?.libraryTitle && (
                        <>
                            {mapping.fields.map((fieldValue, index) =>
                                renderFieldRow(section, mapping, chosenLibrary, fieldValue, index)
                            )}
                            <Stack.Item>
                                <DefaultButton
                                    iconProps={{ iconName: "Add" }}
                                    text={languageStrings.AddMetadataField}
                                    disabled={!sectionEditable || !chosenLibrary || mapping.fields.some((f) => !f.internalName)}
                                    onClick={() => addField(section.key)}
                                />
                            </Stack.Item>
                        </>
                    )}
                </Stack>
            </div>
        );
    };

    const renderRailRow = (row: IMappingRow) => {
        const mappedCount = typeSections.filter((section) => mappingByKey[mappingKey(row.key, section.key)]?.libraryTitle).length;
        const isSelected = selectedRow?.key === row.key;
        const isIgnored = ignoredKeys.has(row.key);
        return (
            <button
                key={row.key}
                type="button"
                className={isSelected ? `${railRowStyle} ${railRowSelectedStyle}` : railRowStyle}
                onClick={() => setSelectedKey(row.key)}
            >
                <Stack horizontal verticalAlign="center" tokens={{ childrenGap: 6 }} styles={{ root: { minWidth: 0 } }}>
                    {row.isStale && (
                        <TooltipHost content={languageStrings.NoLongerInHix}>
                            <Icon iconName="Warning" className={warningIconStyle} />
                        </TooltipHost>
                    )}
                    <span className={railRowLabelStyle} title={row.label}>
                        {row.label}
                    </span>
                </Stack>
                <span className={!isIgnored && mappedCount > 0 ? mappedPillStyle : unmappedPillStyle}>
                    {isIgnored
                        ? languageStrings.Ignored
                        : mappedCount > 0
                            ? `${mappedCount}/${typeSections.length}`
                            : languageStrings.Unmapped}
                </span>
            </button>
        );
    };

    const siteDesignMessage =
        siteDesignStatus === "notConfigured"
            ? languageStrings.SiteDesignNotConfigured
            : siteDesignStatus === "tenantNotFound"
                ? languageStrings.TenantUnknownToProvisioning
                : siteDesignStatus === "unavailable"
                    ? languageStrings.SiteDesignUnavailable
                    : undefined;

    return (
        <Panel
            headerText={`${languageStrings.HixMappingsTitle} — ${tenantName}`}
            isOpen={isOpen}
            onDismiss={attemptClose}
            closeButtonAriaLabel={languageStrings.Close}
            type={PanelType.custom}
            customWidth={"calc(100vw - 208px)"}
            isLightDismiss={false}
        >
            {isLoading ? (
                <Spinner size={SpinnerSize.large} styles={{ root: { marginTop: 40 } }} />
            ) : (
                <>
                    <Stack tokens={{ childrenGap: 8 }} styles={{ root: { marginTop: 10 } }}>
                        {siteDesignMessage && (
                            <MessageBar
                                messageBarType={
                                    siteDesignStatus === "unavailable" ? MessageBarType.error : MessageBarType.severeWarning
                                }
                            >
                                {siteDesignMessage}
                            </MessageBar>
                        )}
                        {mappingsLoadFailed && (
                            <MessageBar messageBarType={MessageBarType.error}>{languageStrings.MappingsLoadFailed}</MessageBar>
                        )}
                        {!hixConfigured && (
                            <MessageBar messageBarType={MessageBarType.warning}>
                                {languageStrings.HixUploadTypesUnavailable}
                            </MessageBar>
                        )}
                        {saveFailed && (
                            <MessageBar messageBarType={MessageBarType.error} onDismiss={() => setSaveFailed(false)}>
                                {languageStrings.MappingsSaveFailed}
                            </MessageBar>
                        )}
                    </Stack>
                    <div className={layoutStyle}>
                        <div className={railStyle}>
                            <span className={railGroupHeaderStyle}>{languageStrings.StandardTaskTypes}</span>
                            {rows.filter((r) => r.kind === "task" && !r.isCustom).map(renderRailRow)}
                            {rows.some((r) => r.kind === "task" && r.isCustom) && (
                                <span className={railGroupHeaderStyle}>{languageStrings.CustomTaskTypes}</span>
                            )}
                            {rows.filter((r) => r.kind === "task" && r.isCustom).map(renderRailRow)}
                            <span className={railSectionHeaderStyle}>{languageStrings.DocumentTypes}</span>
                            {DOCUMENT_TYPE_GROUPS.map((group) => (
                                <React.Fragment key={group.headerKey}>
                                    <span className={railGroupHeaderStyle}>{languageStrings[group.headerKey]}</span>
                                    {group.types
                                        .map((t) => rows.find((r) => r.key === documentRowKey(t.key)))
                                        .filter((r): r is IMappingRow => !!r)
                                        .map(renderRailRow)}
                                </React.Fragment>
                            ))}
                            {rows
                                .filter((r) => r.kind === "document" && !DOCUMENT_TYPE_BY_ROW_KEY.has(r.key))
                                .map(renderRailRow)}
                            <span className={railSectionHeaderStyle}>{languageStrings.TemplateCategories}</span>
                            {rows.filter((r) => r.kind === "templateCategory").map(renderRailRow)}
                            <span className={railSectionHeaderStyle}>{languageStrings.QuestionCategories}</span>
                            {rows.filter((r) => r.kind === "questionCategory").map(renderRailRow)}
                        </div>
                        <div className={detailPaneStyle}>
                            {selectedRow && (
                                <Stack tokens={{ childrenGap: 14 }}>
                                    <Stack horizontal verticalAlign="center" horizontalAlign="space-between">
                                        <Stack styles={{ root: { minWidth: 0 } }}>
                                            <Label styles={{ root: { fontSize: 16, fontWeight: FontWeights.semibold } }}>
                                                {selectedRow.label}
                                            </Label>
                                            {selectedRow.kind === "document" && (
                                                <span style={{ fontSize: 12, opacity: 0.75 }}>
                                                    {documentTypeKeyFromRow(selectedRow.key)}
                                                    {selectedDocDef && ` — ${selectedDocDef.description}`}
                                                </span>
                                            )}
                                            {selectedRow.kind === "templateCategory" && (
                                                <span style={{ fontSize: 12, opacity: 0.75 }}>
                                                    {templateCategoryKeyFromRow(selectedRow.key)}
                                                </span>
                                            )}
                                            {selectedRow.kind === "questionCategory" && (
                                                <span style={{ fontSize: 12, opacity: 0.75 }}>
                                                    {questionCategoryKeyFromRow(selectedRow.key)}
                                                </span>
                                            )}
                                        </Stack>
                                        <Toggle
                                            label={
                                                selectedRow.kind === "document"
                                                    ? languageStrings.IgnoreDocumentType
                                                    : selectedRow.kind === "task"
                                                        ? languageStrings.IgnoreTaskType
                                                        : languageStrings.IgnoreCategory
                                            }
                                            inlineLabel
                                            checked={isSelectedIgnored}
                                            // canEdit, not canEditSelected — the toggle must stay
                                            // usable on an ignored type to un-ignore it.
                                            disabled={!canEdit}
                                            styles={{ root: { marginBottom: 0 } }}
                                            onChange={(_, checked) => toggleIgnored(selectedRow.key, checked === true)}
                                        />
                                    </Stack>
                                    {isSelectedIgnored && (
                                        <MessageBar messageBarType={MessageBarType.info}>
                                            {selectedRow.kind === "document"
                                                ? languageStrings.IgnoredDocumentTypeInfo
                                                : selectedRow.kind === "task"
                                                    ? languageStrings.IgnoredTaskTypeInfo
                                                    : languageStrings.IgnoredCategoryInfo}
                                        </MessageBar>
                                    )}
                                    {typeSections.map(renderTypeSection)}
                                </Stack>
                            )}
                        </div>
                    </div>
                    <Stack horizontal horizontalAlign="end" tokens={{ childrenGap: 10 }} styles={{ root: { marginTop: 12 } }}>
                        <PrimaryButton
                            text={languageStrings.Save}
                            disabled={!isDirty || !canEdit || isSaving}
                            onClick={onSave}
                        >
                            {isSaving && <Spinner size={SpinnerSize.small} style={{ marginLeft: 10 }} />}
                        </PrimaryButton>
                        <DefaultButton text={languageStrings.Cancel} onClick={attemptClose} />
                    </Stack>
                </>
            )}
            {copyConfirmSource && (() => {
                const preview = buildCopyPreview(copyConfirmSource);
                const anySettable = preview.some((p) => p.willSet);
                return (
                    <Dialog
                        hidden={false}
                        onDismiss={() => setCopyConfirmSource(null)}
                        dialogContentProps={{
                            type: DialogType.normal,
                            title: languageStrings.CopyToOtherTypes,
                            closeButtonAriaLabel: languageStrings.Close,
                            subText: languageStrings.CopyConfirmIntro,
                        }}
                        modalProps={{ isBlocking: true, styles: { main: { maxWidth: 560 } } }}
                    >
                        <Stack tokens={{ childrenGap: 6 }} styles={{ root: { marginBottom: 6 } }}>
                            {preview.map((p) => (
                                <Stack key={p.typeKey} horizontal tokens={{ childrenGap: 8 }} verticalAlign="start">
                                    <Icon
                                        iconName={p.willSet ? "Accept" : "Blocked2"}
                                        className={p.willSet ? undefined : warningIconStyle}
                                        style={{ marginTop: 3, flexShrink: 0 }}
                                    />
                                    <span style={{ fontSize: 13 }}>
                                        <b>{p.typeKey}</b>: {p.text}
                                    </span>
                                </Stack>
                            ))}
                        </Stack>
                        <DialogFooter>
                            <PrimaryButton
                                text={languageStrings.Yes}
                                disabled={!anySettable}
                                onClick={() => {
                                    copyToOtherTypes(copyConfirmSource);
                                    setCopyConfirmSource(null);
                                }}
                            />
                            <DefaultButton text={languageStrings.Cancel} onClick={() => setCopyConfirmSource(null)} />
                        </DialogFooter>
                    </Dialog>
                );
            })()}
            {showCloseConfirm && (
                <DialogMessage
                    isHidden={!showCloseConfirm}
                    hideDialog={() => setShowCloseConfirm(false)}
                    title={languageStrings.EditMappings}
                    message={languageStrings.UnsavedChangesLost}
                    confirmAction={() => {
                        setShowCloseConfirm(false);
                        onClose();
                    }}
                    cancelAction={() => setShowCloseConfirm(false)}
                    type={MessageType.YesNo}
                />
            )}
        </Panel>
    );
};

export default HixMappingsPanel;
